import React, { useEffect, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  Send, Square, Copy, RefreshCw, ThumbsUp, ThumbsDown, Paperclip,
  LifeBuoy, Trash2, Loader2, BookOpen, Check,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function FlowAvatar({ size = "h-7 w-7" }) {
  return (
    <div className={`${size} shrink-0 rounded-full bg-brand-amber/15 border border-brand-amber/40 flex items-center justify-center`}>
      <span className="font-display font-black text-brand-amber text-[11px]">F</span>
    </div>
  );
}

function Message({ m, onCopy, onFeedback, onRegenerate, isLast, copied }) {
  const isUser = m.role === "user";
  return (
    <div className={`flex gap-2.5 ${isUser ? "justify-end" : ""}`} data-testid={`flow-msg-${m.role}`}>
      {!isUser && <FlowAvatar />}
      <div className={`max-w-[85%] ${isUser ? "order-1" : ""}`}>
        <div className={`rounded-2xl px-3.5 py-2.5 text-sm whitespace-pre-wrap leading-relaxed ${
          isUser ? "bg-brand-amber text-black rounded-br-md" : "bg-bg-elevated border border-line text-ink-primary rounded-bl-md"}`}>
          {m.content}
          {m.streaming && <span className="inline-block w-1.5 h-4 bg-brand-amber ml-0.5 animate-pulse align-text-bottom" />}
        </div>
        {!isUser && (m.sources || []).length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-1.5">
            {m.sources.filter((s) => s.url).map((s, i) => (
              <Link key={i} to={s.url} className="inline-flex items-center gap-1 rounded-full border border-line bg-bg-surface px-2 py-0.5 text-[11px] text-ink-secondary hover:text-brand-amber hover:border-brand-amber/40 transition" data-testid="flow-source-link">
                <BookOpen className="h-3 w-3" /> {s.title}
              </Link>
            ))}
          </div>
        )}
        {!isUser && !m.streaming && m.message_id && (
          <div className="flex items-center gap-1 mt-1">
            <button onClick={() => onCopy(m)} className="p-1 rounded text-ink-muted hover:text-ink-primary transition" title="Copy response" aria-label="Copy response" data-testid="flow-copy-btn">
              {copied === m.message_id ? <Check className="h-3.5 w-3.5 text-emerald-400" /> : <Copy className="h-3.5 w-3.5" />}
            </button>
            {isLast && (
              <button onClick={() => onRegenerate(m)} className="p-1 rounded text-ink-muted hover:text-ink-primary transition" title="Regenerate response" aria-label="Regenerate response" data-testid="flow-regenerate-btn">
                <RefreshCw className="h-3.5 w-3.5" />
              </button>
            )}
            <button onClick={() => onFeedback(m, "up")} className={`p-1 rounded transition ${m.my_rating === "up" ? "text-emerald-400" : "text-ink-muted hover:text-ink-primary"}`} title="Good response" aria-label="Thumbs up" data-testid="flow-thumbs-up">
              <ThumbsUp className="h-3.5 w-3.5" />
            </button>
            <button onClick={() => onFeedback(m, "down")} className={`p-1 rounded transition ${m.my_rating === "down" ? "text-red-400" : "text-ink-muted hover:text-ink-primary"}`} title="Poor response" aria-label="Thumbs down" data-testid="flow-thumbs-down">
              <ThumbsDown className="h-3.5 w-3.5" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function ChatView({ conversationId, onConversationChange, module = "", pageTitle = "", bootstrap, compact = false }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState("");
  const [clearOpen, setClearOpen] = useState(false);
  const [escalate, setEscalate] = useState(null); // {subject, summary}
  const [escalateBusy, setEscalateBusy] = useState(false);
  const abortRef = useRef(null);
  const bottomRef = useRef(null);
  const cidRef = useRef(conversationId);
  cidRef.current = conversationId;

  useEffect(() => {
    if (!conversationId) { setMessages([]); return; }
    api.get(`/assistant/conversations/${conversationId}/messages`)
      .then((r) => setMessages(r.data?.messages || []))
      .catch(() => setMessages([]));
  }, [conversationId]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const stream = useCallback(async (text, regenerateOf = null) => {
    setBusy(true);
    const controller = new AbortController();
    abortRef.current = controller;
    if (!regenerateOf) {
      setMessages((ms) => [...ms, { role: "user", content: text, message_id: `tmp_${Date.now()}` }]);
    }
    setMessages((ms) => [...ms, { role: "assistant", content: "", streaming: true, message_id: null }]);
    try {
      const res = await fetch(`${BACKEND}/api/assistant/chat`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ conversation_id: cidRef.current || null, message: text, module, page_title: pageTitle, regenerate_of: regenerateOf }),
        signal: controller.signal,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Flow is unavailable right now.");
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n\n");
        buffer = lines.pop();
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const ev = JSON.parse(line.slice(6));
          if (ev.type === "delta") {
            setMessages((ms) => {
              const copy = [...ms];
              copy[copy.length - 1] = { ...copy[copy.length - 1], content: copy[copy.length - 1].content + ev.text };
              return copy;
            });
          } else if (ev.type === "done") {
            setMessages((ms) => {
              const copy = [...ms];
              copy[copy.length - 1] = { ...copy[copy.length - 1], streaming: false, message_id: ev.message_id, sources: ev.sources || [] };
              return copy;
            });
            if (!cidRef.current && ev.conversation_id) onConversationChange?.(ev.conversation_id);
          } else if (ev.type === "error") {
            throw new Error(ev.message);
          }
        }
      }
    } catch (e) {
      if (e.name === "AbortError") {
        setMessages((ms) => ms.map((m, i) => (i === ms.length - 1 ? { ...m, streaming: false, content: m.content || "(stopped)" } : m)));
      } else {
        setMessages((ms) => ms.filter((m) => !(m.streaming && !m.content)));
        toast.error(e.message || "Flow is unavailable right now.");
      }
    } finally {
      setMessages((ms) => ms.map((m) => ({ ...m, streaming: false })));
      setBusy(false);
      abortRef.current = null;
    }
  }, [module, pageTitle, onConversationChange]);

  const send = (text) => {
    const t = (text ?? input).trim();
    if (!t || busy) return;
    setInput("");
    stream(t);
  };

  const handleStop = () => abortRef.current?.abort();

  const handleCopy = async (m) => {
    try {
      await navigator.clipboard.writeText(m.content);
      setCopied(m.message_id);
      setTimeout(() => setCopied(""), 1500);
    } catch { toast.error("Couldn't copy"); }
  };

  const handleFeedback = async (m, rating) => {
    try {
      await api.post(`/assistant/messages/${m.message_id}/feedback`, { rating });
      setMessages((ms) => ms.map((x) => (x.message_id === m.message_id ? { ...x, my_rating: rating } : x)));
      toast.success("Thanks for the feedback!");
    } catch { toast.error("Couldn't save feedback"); }
  };

  const handleRegenerate = (m) => {
    const lastUser = [...messages].reverse().find((x) => x.role === "user");
    if (!lastUser || busy) return;
    setMessages((ms) => ms.filter((x) => x.message_id !== m.message_id));
    stream(lastUser.content, m.message_id);
  };

  const handleClear = async () => {
    setClearOpen(false);
    if (cidRef.current) {
      try { await api.delete(`/assistant/conversations/${cidRef.current}`); } catch { /* noop */ }
    }
    setMessages([]);
    onConversationChange?.(null);
  };

  const openEscalate = async () => {
    if (!cidRef.current) { toast.info("Start a conversation first so support can see the context."); return; }
    try {
      const r = await api.post("/assistant/escalate/preview", { conversation_id: cidRef.current });
      setEscalate(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't prepare the support request"); }
  };

  const confirmEscalate = async () => {
    setEscalateBusy(true);
    try {
      const r = await api.post("/assistant/escalate/confirm", {
        conversation_id: cidRef.current, subject: escalate.subject, summary: escalate.summary,
      });
      toast.success(`Support ticket ${r.data.reference} created — the team will reply by email.`);
      setEscalate(null);
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't create the ticket"); }
    finally { setEscalateBusy(false); }
  };

  const lastAssistantId = [...messages].reverse().find((m) => m.role === "assistant" && m.message_id)?.message_id;
  const prompts = bootstrap?.suggested_prompts || [];

  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4 min-h-0" data-testid="flow-messages">
        {messages.length === 0 && (
          <div className="space-y-4">
            <div className="flex gap-2.5">
              <FlowAvatar />
              <div className="rounded-2xl rounded-bl-md bg-bg-elevated border border-line px-3.5 py-2.5 text-sm text-ink-primary max-w-[85%]" data-testid="flow-welcome">
                {bootstrap?.welcome_message || "Hi, I'm Flow — how can I help?"}
              </div>
            </div>
            <div className="flex flex-wrap gap-2 pl-9" data-testid="flow-suggested-prompts">
              {prompts.map((p) => (
                <button key={p} onClick={() => send(p)}
                        className="rounded-full border border-line bg-bg-surface px-3 py-1.5 text-xs text-ink-secondary hover:text-brand-amber hover:border-brand-amber/40 transition"
                        data-testid="flow-prompt-chip">
                  {p}
                </button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m, i) => (
          <Message key={m.message_id || i} m={m} onCopy={handleCopy} onFeedback={handleFeedback}
                   onRegenerate={handleRegenerate} isLast={m.message_id === lastAssistantId} copied={copied} />
        ))}
        <div ref={bottomRef} />
      </div>

      <div className="border-t border-line px-3 py-2.5 bg-bg-surface/60">
        <div className="flex items-end gap-2">
          <button className="p-2 rounded-md text-ink-muted cursor-not-allowed" title="Attachments — Coming Soon" aria-label="Attachments (coming soon)" disabled data-testid="flow-attachment-btn">
            <Paperclip className="h-4 w-4" />
          </button>
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder="Ask Flow anything…"
            rows={1}
            className="flex-1 resize-none rounded-md border border-line bg-bg-elevated px-3 py-2 text-sm text-ink-primary placeholder:text-ink-muted focus:outline-none focus:border-brand-amber/60 transition max-h-28"
            aria-label="Message BroadcastFlow Assistant"
            data-testid="flow-input"
          />
          {busy ? (
            <button onClick={handleStop} className="p-2 rounded-md bg-red-500/15 border border-red-500/40 text-red-400 hover:bg-red-500/25 transition" title="Stop generating" aria-label="Stop generating" data-testid="flow-stop-btn">
              <Square className="h-4 w-4" />
            </button>
          ) : (
            <button onClick={() => send()} disabled={!input.trim()}
                    className="p-2 rounded-md bg-brand-amber text-black hover:bg-brand-amber/90 disabled:opacity-40 transition" title="Send" aria-label="Send message" data-testid="flow-send-btn">
              <Send className="h-4 w-4" />
            </button>
          )}
        </div>
        <div className="flex items-center justify-between mt-1.5 px-1">
          <div className="flex items-center gap-2">
            <button onClick={() => setClearOpen(true)} className="inline-flex items-center gap-1 text-[11px] text-ink-muted hover:text-red-400 transition" data-testid="flow-clear-btn">
              <Trash2 className="h-3 w-3" /> Clear
            </button>
            <button onClick={openEscalate} className="inline-flex items-center gap-1 text-[11px] text-ink-muted hover:text-brand-amber transition" data-testid="flow-support-btn">
              <LifeBuoy className="h-3 w-3" /> Contact support
            </button>
          </div>
          <span className="text-[10px] text-ink-muted" data-testid="flow-privacy-note">
            {compact ? "AI answers can make mistakes." : "Conversations are private to your account. AI answers can make mistakes — verify important details."}
          </span>
        </div>
      </div>

      <AlertDialog open={clearOpen} onOpenChange={setClearOpen}>
        <AlertDialogContent className="bg-bg-surface border-line" data-testid="flow-clear-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Clear this conversation?</AlertDialogTitle>
            <AlertDialogDescription>This deletes the conversation and its messages permanently.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="flow-clear-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleClear} className="bg-red-500 hover:bg-red-600 text-white" data-testid="flow-clear-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!escalate} onOpenChange={(o) => !o && setEscalate(null)}>
        <AlertDialogContent className="bg-bg-surface border-line max-w-lg" data-testid="flow-escalate-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Create a support request?</AlertDialogTitle>
            <AlertDialogDescription>
              A human will pick this up. Review what will be sent — nothing is created until you confirm.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {escalate && (
            <div className="space-y-2 text-sm">
              <input value={escalate.subject} onChange={(e) => setEscalate({ ...escalate, subject: e.target.value })}
                     className="w-full rounded-md border border-line bg-bg-elevated px-3 py-2 text-sm" data-testid="flow-escalate-subject" />
              <textarea value={escalate.summary} onChange={(e) => setEscalate({ ...escalate, summary: e.target.value })}
                        rows={7} className="w-full rounded-md border border-line bg-bg-elevated px-3 py-2 text-xs font-mono" data-testid="flow-escalate-summary" />
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="flow-escalate-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmEscalate} disabled={escalateBusy}
                               className="bg-brand-amber hover:bg-brand-amber/90 text-black" data-testid="flow-escalate-confirm">
              {escalateBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create ticket"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
